import { MasterOption } from "../types/masters";
import { Pagination } from "../types/pagination";
import { IDropdownOptionsService } from "../types/services/dropdown-options-service";
import { FetchResult, GET } from "../utils/fetch";

class DropdownOptionsService implements IDropdownOptionsService {
  async getStaffOptions(
    verticals?: string[],
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/staffs/list",
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }
    if (verticals && verticals.length) {
      verticals.forEach((vertical) => qp.append("verticals[]", vertical));
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<StaffOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.staffs,
      paginate: response.paginate,
      error: null,
    };
  }

  async getStaffOptionById(id: string): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/staffs/${id}`,
      { response, error } = await GET<StaffOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    if (!response.staff) {
      return {
        response: null,
        error: new Error("Staff not found"),
      };
    }

    const { name, value } = response.staff,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getSipStaffOptions(
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/staffs/poc_list",
      qp = new URLSearchParams();

    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }

    const qpM = qp.toString();

    let url = base;

    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<StaffOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const formatted = response.staffs.map((each: any) => {
      const { name, sip } = each;

      const formatted: MasterOption = {
        name: name || "-",
        value: sip || "",
      };

      return formatted;
    });

    return {
      response: formatted,
      paginate: response.paginate,
      error: null,
    };
  }

  async getSipStaffOptionById(id: string): Promise<FetchResult<MasterOption>> {
    const base = "/api/v2/staffs/poc_list",
      qp = new URLSearchParams();

    qp.set("view", "dropdown");

    qp.set("query", id);

    const qpM = qp.toString();

    let url = base;

    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<StaffOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, value } = response.staffs[0] ?? {},
      formatted: MasterOption = {
        name: name ?? "-",
        value: value ?? id,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getAgentOptions(
    vertical?: string,
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/agents/list",
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }
    if (vertical) {
      qp.set("vertical", vertical);
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<AgentOptionsPaginated>(url);
    if (error) {
      return {
        response: null,
        error,
      };
    }
    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.agents,
      paginate: response.paginate,
      error: null,
    };
  }

  async getAgentOptionById(id: string): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/agents/${id}/dropdown`,
      { response, error } = await GET<AgentOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, value } = response.agent,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getHomeownerOptions(
    verticals?: string[],
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/rental/home_owners/list",
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }
    if (verticals && verticals.length) {
      verticals.forEach((vertical) => qp.append("verticals[]", vertical));
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<HomeownerPaginated>(url);
    if (error) {
      return {
        response: null,
        error,
      };
    }
    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.home_owners,
      paginate: response.paginate,
      error: null,
    };
  }

  async getHomeownerOptionById(id: string): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/rental/home_owners/${id}`,
      { response, error } = await GET<HomeownerOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const formatted: MasterOption = {
      name: response.home_owner.name,
      value: response.home_owner.id,
    };

    return {
      response: formatted,
      error: null,
    };
  }

  async getVendorOptions(query?: string): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/vendors",
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }

    const qpM = qp.toString();

    let url = base;

    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<VendorOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const vendors = response.vendors.map((each) => {
      const option: MasterOption = {
        name: each.name,
        value: each.id,
      };

      return option;
    });

    return {
      response: vendors,
      paginate: response.paginate,
      error: null,
    };
  }

  async getVendorOptionById(id: string): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/vendors/${id}`,
      { response, error } = await GET<VendorOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, id: value } = response.vendor,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getCategoryOptions(
    query?: string,
    department_id?: string,
    include_id?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/categories",
      qp = new URLSearchParams();

    if (query) {
      qp.set("query", query);
    }

    if (department_id) {
      qp.set("department_id", department_id);
    }

    if (include_id) {
      qp.set("include_id", include_id);
    }

    const qpM = qp.toString();

    let url = base;

    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<CategoryOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const categories = response.categories.map((each) => {
      const option: MasterOption = {
        name: each.name,
        value: each.id,
        read_only: each.read_only,
      };

      return option;
    });

    return {
      response: categories,
      paginate: response.paginate,
      error: null,
    };
  }

  async getCategoryOptionById(id: string): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/categories/${id}`,
      { response, error } = await GET<CategoryOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, id: value } = response.category,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getSubCategoryOptions(
    category: string,
    query?: string,
    department_id?: string,
    include_id?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/categories",
      qp = new URLSearchParams();

    qp.set("parent_category_id", category);

    if (query) {
      qp.set("query", query);
    }

    if (department_id) {
      qp.set("department_id", department_id);
    }

    if (include_id) {
      qp.set("include_id", include_id);
    }

    const qpM = qp.toString();

    let url = base;

    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<SubCategoryOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const categories = response.categories.map((each) => {
      const option: MasterOption = {
        name: each.name,
        value: each.id,
        read_only: each.read_only,
      };

      return option;
    });

    return {
      response: categories,
      paginate: response.paginate,
      error: null,
    };
  }

  async getSubCategoryOptionById(
    id: string,
  ): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/categories/${id}`;

    const { response, error } = await GET<SubCategoryOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, id: value } = response.category,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getPropertyOptions(
    vertical?: string,
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = `/api/v2/${vertical}/properties/list`,
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<PropertyOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.properties,
      paginate: response.paginate,
      error: null,
    };
  }

  async getPropertyOptionById(
    id: string,
    vertical?: string,
  ): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/${vertical}/properties/${id}`,
      { response, error } = await GET<PropertyOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, value } = response.property,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getPlotOptions(
    verticals?: string[],
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/plots",
      qp = new URLSearchParams();

    if (query) {
      qp.set("query", query);
    }
    if (verticals && verticals.length) {
      verticals.forEach((vertical) => qp.append("verticals[]", vertical));
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<PlotOptionsPaginated>(url);
    if (error) {
      return {
        response: null,
        error,
      };
    }
    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.plots,
      error: null,
    };
  }

  async getHouseKeepingOptions(
    verticals?: string[],
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/rental/housekeeping_staff",
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }
    if (verticals && verticals.length) {
      verticals.forEach((vertical) => qp.append("verticals[]", vertical));
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<StaffOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.staffs,
      paginate: response.paginate,
      error: null,
    };
  }

  async getHouseKeepingStaffOptionById(
    id: string,
  ): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/rental/housekeeping_staff/${id}`,
      { response, error } = await GET<StaffOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, value } = response.staff,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getMaintenanceOptions(
    verticals?: string[],
    query?: string,
  ): Promise<FetchResult<MasterOption[]>> {
    const base = "/api/v2/rental/maintenance_staff",
      qp = new URLSearchParams();
    qp.set("view", "dropdown");

    if (query) {
      qp.set("query", query);
    }
    if (verticals && verticals.length) {
      verticals.forEach((vertical) => qp.append("verticals[]", vertical));
    }

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<StaffOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    return {
      response: response.staffs,
      paginate: response.paginate,
      error: null,
    };
  }

  async getMaintenanceStaffOptionById(
    id: string,
  ): Promise<FetchResult<MasterOption>> {
    const url = `/api/v2/rental/maintenance_staff/${id}`,
      { response, error } = await GET<StaffOptionById>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const { name, value } = response.staff,
      formatted: MasterOption = {
        name: name,
        value: value,
      };

    return {
      response: formatted,
      error: null,
    };
  }

  async getCityOptions(query?: string): Promise<FetchResult<MasterOption[]>> {

    const base = "/api/v2/cities",
      qp = new URLSearchParams();

    if (query) {
      qp.set("query", query);
    }
    qp.set("page", "1");
    qp.set("per_page", "20");

    const qpM = qp.toString();
    let url = base;
    if (qpM) {
      url = `${base}?${qpM}`;
    }

    const { response, error } = await GET<CityOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const cities = response.cities.map((each) => {
      const option: MasterOption = {
        name: each.name,
        value: each.name,
      };

      return option;
    });

    return {
      response: cities,
      paginate: response.paginate,
      error: null,
    };
  }

  async getCityOptionByName(name: string): Promise<FetchResult<MasterOption>> {

    if (!name) {
      return {
        response: null,
        error: null,
      };
    }

    // Call API directly using 'name' query parameter for exact match
    const base = "/api/v2/cities",
      qp = new URLSearchParams();

    qp.set("name", name);
    qp.set("page", "1");
    qp.set("per_page", "20");

    const qpM = qp.toString();
    const url = `${base}?${qpM}`;

    const { response, error } = await GET<CityOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error: error,
      };
    }

    if (!response || !response.cities || response.cities.length === 0) {
      return {
        response: null,
        error: null,
      };
    }

    // Find exact match
    const exactMatch = response.cities.find(city => city.name === name);
    if (exactMatch) {
      return {
        response: {
          name: exactMatch.name,
          value: exactMatch.name,
        },
        error: null,
      };
    }

    // Return first match if no exact match
    const firstCity = response.cities[0];
    return {
      response: {
        name: firstCity.name,
        value: firstCity.name,
      },
      error: null,
    };
  }

  async getDevelopmentPropertyOptions(
    query?: string,
    selectedIds?: string[],
  ): Promise<FetchResult<MasterOption[]>> {
    const qp = new URLSearchParams();

    if (query) {
      qp.set("query", query);
    }
    if (selectedIds && selectedIds.length) {
      selectedIds.forEach((id) => qp.append("interested_project_ids[]", id));
    }

    const qpM = qp.toString();
    let url = "/api/v2/development/properties";
    if (qpM) {
      url = `${url}?${qpM}`;
    }

    const { response, error } = await GET<DevelopmentPropertyOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const formatted: MasterOption[] = response.properties.map((each) => ({
      name: each.status === "SoldOut" ? `${each.name} (Sold Out)` : each.name || "",
      value: each.id.toString(),
    }));

    return {
      response: formatted,
      error: null,
    };
  }

  async getChapterPropertyOptions(
    query?: string,
    selectedIds?: string[],
  ): Promise<FetchResult<MasterOption[]>> {
    const qp = new URLSearchParams();

    if (query) {
      qp.set("query", query);
    }
    if (selectedIds && selectedIds.length) {
      selectedIds.forEach((id) => qp.append("interested_project_ids[]", id));
    }

    const qpM = qp.toString();
    let url = "/api/v2/chapter/properties";
    if (qpM) {
      url = `${url}?${qpM}`;
    }

    const { response, error } = await GET<DevelopmentPropertyOptionsPaginated>(url);

    if (error) {
      return {
        response: null,
        error,
      };
    }

    if (!response) {
      return {
        response: null,
        error: new Error("Unknown error"),
      };
    }

    const formatted: MasterOption[] = response.properties.map((each) => ({
      name: each.status === "SoldOut" ? `${each.name} (Sold Out)` : each.name || "",
      value: each.id.toString(),
    }));

    return {
      response: formatted,
      error: null,
    };
  }
}

export default DropdownOptionsService;

type StaffOptionsPaginated = {
  staffs: MasterOption[];
  paginate: Pagination;
};

type StaffOptionById = {
  staff: any;
};

type AgentOptionsPaginated = {
  agents: MasterOption[];
  paginate: Pagination;
};

type AgentOptionById = {
  agent: any;
};

type HomeownerPaginated = {
  home_owners: MasterOption[];
  paginate: Pagination;
};

type HomeownerOptionById = {
  home_owner: any;
};

type VendorOptionsPaginated = {
  vendors: any[];
  paginate: Pagination;
};

type VendorOptionById = {
  vendor: any;
};

type CategoryOptionsPaginated = {
  categories: any[];
  paginate: Pagination;
};

type CategoryOptionById = {
  category: any;
};

type SubCategoryOptionsPaginated = {
  categories: any[];
  paginate: Pagination;
};

type SubCategoryOptionById = {
  category: any;
};

type PropertyOptionsPaginated = {
  properties: MasterOption[];
  paginate: Pagination;
};

type PropertyOptionById = {
  property: any;
};

type PlotOptionsPaginated = {
  plots: MasterOption[];
};

type CityOptionsPaginated = {
  cities: any[];
  paginate: Pagination;
};

type DevelopmentPropertyOptionsPaginated = {
  properties: Array<{ id: number; name: string; status: string }>;
  paginate: Pagination;
};
